import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { chargerRequestsApi, companiesApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Filter, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';
import {
  Page, GlassCard, PageHeader, SectionHeading, EmptyState, ErrorState, PageSkeleton, LoadingRows,
  SearchField, Field, RequestStatusBadge, REQUEST_STATUSES, glassStrong, errorMessage
} from '@/components/glass/surfaces';

const emptyForm = {
  charger_name: '',
  charge_point_id: '',
  charger_type: '',
  capacity_kw: '',
  connector_count: 1,
  location: '',
  zone: '',
  site_name: '',
  assigned_company_id: ''
};

const formFields = [
  ['charger_name', 'Charger name', 'text'],
  ['charge_point_id', 'OCPP charger ID', 'text'],
  ['charger_type', 'Type / model', 'text'],
  ['capacity_kw', 'Capacity (kW)', 'number'],
  ['connector_count', 'Connector count', 'number'],
  ['location', 'Location', 'text'],
  ['zone', 'Zone', 'text'],
  ['site_name', 'Site / company', 'text']
];

const ChargerRequestsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();
  const [requests, setRequests] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [resultsFor, setResultsFor] = useState(status);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [requestResponse, companyResponse] = await Promise.all([
          chargerRequestsApi.getAll(status === 'all' ? {} : { status }),
          can('companies.view') ? companiesApi.getAll() : Promise.resolve({ data: [] })
        ]);
        if (cancelled) return;
        setRequests(requestResponse.data || []);
        setCompanies(companyResponse.data || []);
        setResultsFor(status);
        setError('');
      } catch (fetchError) {
        if (cancelled) return;
        setError(errorMessage(fetchError, 'Failed to load charger requests'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [status, can, reloadToken]);

  useEffect(() => {
    if (location.state?.openNewRequest) {
      setOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const filtered = useMemo(() => requests.filter((request) => {
    const text = `${request.charger_name || ''} ${request.charge_point_id || ''} ${request.location || ''} ${request.site_name || ''}`.toLowerCase();
    return text.includes(search.toLowerCase());
  }), [requests, search]);

  const missingRequired = !form.charger_name.trim() || !form.charge_point_id.trim() || !form.location.trim() || !form.assigned_company_id;
  const isFiltered = Boolean(search) || status !== 'all';

  const create = async (event) => {
    event.preventDefault();
    const chargePointId = form.charge_point_id.trim();
    if (/\s/.test(chargePointId)) {
      toast.error('OCPP charger ID cannot contain spaces');
      return;
    }
    const connectorCount = Number(form.connector_count) || 1;
    if (connectorCount < 1 || connectorCount > 20) {
      toast.error('Connector count must be between 1 and 20');
      return;
    }
    setSaving(true);
    try {
      const response = await chargerRequestsApi.create({
        ...form,
        charger_name: form.charger_name.trim(),
        charge_point_id: chargePointId,
        location: form.location.trim(),
        capacity_kw: form.capacity_kw === '' ? null : Number(form.capacity_kw),
        connector_count: connectorCount,
        site_details: {},
        technical_information: {},
        commercial_information: {}
      });
      setOpen(false);
      setForm(emptyForm);
      navigate(`/charger-requests/${response.data.id}`);
    } catch (createError) {
      toast.error(errorMessage(createError, 'Failed to create request'));
    } finally {
      setSaving(false);
    }
  };

  if (loading && requests.length === 0 && !error) {
    return <PageSkeleton testId="charger-requests-loading" cards={2} />;
  }

  return (
    <Page testId="charger-requests-page">
      <PageHeader
        title="Charger Requests"
        description="Create, review, and track charger onboarding."
        actions={can('requests.create') ? (
          <Button type="button" className="rounded-full" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" />
            New Request
          </Button>
        ) : null}
      />

      {error ? (
        <ErrorState
          title={requests.length ? 'Request list may be out of date' : 'Could not load charger requests'}
          message={error}
          onRetry={() => { setLoading(requests.length === 0); setReloadToken((value) => value + 1); }}
        />
      ) : null}

      <GlassCard tone="subtle" className="p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <SearchField value={search} onChange={setSearch} placeholder="Search by name, charger ID, or location" />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full rounded-full bg-background/70 sm:w-56">
              <span className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <SelectValue />
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {REQUEST_STATUSES.map((item) => (
                <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </GlassCard>

      {error && requests.length === 0 ? null : (
      <GlassCard className="p-5 sm:p-6">
        <SectionHeading
          title="Requests"
          description={isFiltered ? `${filtered.length} of ${requests.length} shown` : `${requests.length} total`}
        />
        {loading || resultsFor !== status ? (
          <LoadingRows />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Plus}
            title={isFiltered ? 'No requests match' : 'No charger requests yet'}
            description={isFiltered ? 'Try a different search or status.' : 'Start an onboarding request before a charger is installed.'}
            action={isFiltered ? (
              <Button type="button" variant="outline" className="rounded-full" onClick={() => { setSearch(''); setStatus('all'); }}>
                Clear filters
              </Button>
            ) : can('requests.create') ? (
              <Button type="button" className="rounded-full" onClick={() => setOpen(true)}>New Request</Button>
            ) : null}
          />
        ) : (
          <>
            <div className="hidden md:block">
              <Table className="min-w-[760px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Charger</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Revision</TableHead>
                    <TableHead className="whitespace-nowrap">Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((request) => (
                    <TableRow key={request.id} className="cursor-pointer" onClick={() => navigate(`/charger-requests/${request.id}`)}>
                      <TableCell>
                        <div className="font-medium">{request.charger_name}</div>
                        <div className="font-mono text-xs text-muted-foreground">{request.charge_point_id}</div>
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate">{request.location || '-'}</TableCell>
                      <TableCell><RequestStatusBadge status={request.status} /></TableCell>
                      <TableCell>{request.revision}</TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(request.updated_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="space-y-3 md:hidden">
              {filtered.map((request) => (
                <button
                  key={request.id}
                  type="button"
                  className="w-full rounded-2xl bg-black/[0.03] p-4 text-left dark:bg-white/[0.04]"
                  onClick={() => navigate(`/charger-requests/${request.id}`)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium">{request.charger_name}</p>
                      <p className="truncate font-mono text-xs text-muted-foreground">{request.charge_point_id}</p>
                    </div>
                    <RequestStatusBadge status={request.status} />
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{request.location || '-'}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Revision {request.revision} · {formatDate(request.updated_at)}</p>
                </button>
              ))}
            </div>
          </>
        )}
      </GlassCard>
      )}

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (saving) return;
          setOpen(next);
          if (!next) setForm(emptyForm);
        }}
      >
        <DialogContent className={`${glassStrong} max-h-[85vh] max-w-2xl overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>New charger request</DialogTitle>
          </DialogHeader>
          <form className="grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={create}>
            {formFields.map(([key, label, type]) => (
              <Field label={label} key={key}>
                <Input
                  type={type}
                  min={key === 'connector_count' ? 1 : undefined}
                  max={key === 'connector_count' ? 20 : undefined}
                  step={key === 'capacity_kw' ? '0.1' : undefined}
                  value={form[key]}
                  onChange={(event) => setForm({ ...form, [key]: event.target.value })}
                  className="rounded-xl"
                  required={['charger_name', 'charge_point_id', 'location'].includes(key)}
                />
              </Field>
            ))}
            <Field
              className="sm:col-span-2"
              label="Assigned reviewer company"
              hint={companies.length === 0 ? 'No reviewer companies are available for your account.' : 'This company reviews the request before installation.'}
            >
              <Select
                value={form.assigned_company_id || undefined}
                onValueChange={(value) => setForm({ ...form, assigned_company_id: value })}
                disabled={companies.length === 0}
              >
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" className="rounded-full" disabled={saving || missingRequired}>
                {saving ? 'Creating...' : 'Create draft'}
              </Button>
            </DialogFooter>
            {missingRequired ? (
              <p className="text-xs text-muted-foreground sm:col-span-2">Name, OCPP ID, location, and a reviewer company are required.</p>
            ) : null}
          </form>
        </DialogContent>
      </Dialog>
    </Page>
  );
};

export default ChargerRequestsPage;
